import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CAvatar,
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CListGroup,
  CListGroupItem,
  CRow,
  CSpinner,
  CWidgetStatsB,
} from "@coreui/react";

import { apiClient } from "services/Api";
import userAvatar from "assets/images/avatars/user.png";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("pt-PT");
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiClient
      .get("/dashboard/home/")
      .then((response) => {
        setData(response.data);
        setError(false);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const taskCounts = useMemo(() => {
    if (!data)
      return { actions: 0, events: 0, assessments: 0, surveillances: 0 };
    const performanceCount = data.tasks?.performance_assessments?.length || 0;
    const supervisoryCount = data.tasks?.supervisory_assessments?.length || 0;
    return {
      actions: data.tasks?.improvement_actions?.length || 0,
      events: data.tasks?.events?.length || 0,
      assessments: performanceCount + supervisoryCount,
      surveillances: data.interface_surveillance?.length || 0,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="text-center">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <CCard>
        <CCardBody className="text-center text-muted">
          Não foi possível carregar a página inicial.
        </CCardBody>
      </CCard>
    );
  }

  const user = data.user || {};
  const departments = data.departments || [];
  const performanceAssessments = data.tasks?.performance_assessments || [];
  const supervisoryAssessments = data.tasks?.supervisory_assessments || [];
  const assessments = [
    ...performanceAssessments.map((assessment) => ({
      ...assessment,
      type: "desempenho",
    })),
    ...supervisoryAssessments.map((assessment) => ({
      ...assessment,
      type: "chefias",
    })),
  ];

  const roleBadges = [
    user.role ? user.role : null,
    user.is_area_manager ? "Gestor de Área" : null,
    user.is_admin ? "Admin" : null,
    user.is_manager ? "Gerência" : null,
    user.is_director ? "Dir. Qualidade" : null,
  ].filter(Boolean);

  return (
    <CContainer className="px-0">
      <CRow className="g-3 mb-4 align-items-stretch">
        <CCol sm={6} lg={3}>
          <CWidgetStatsB
            className="resources-kpi h-100 kpi-total"
            value={taskCounts.actions}
            title="Ações de melhoria"
            text="Abertas"
          />
        </CCol>
        <CCol sm={6} lg={3}>
          <CWidgetStatsB
            className="resources-kpi h-100 kpi-completion"
            value={taskCounts.events}
            title="Formações"
            text="Aprovadas e por acontecer"
          />
        </CCol>
        <CCol sm={6} lg={3}>
          <CWidgetStatsB
            className="resources-kpi h-100 kpi-overdue"
            value={taskCounts.assessments}
            title="Avaliações"
            text={`Ano ${data.year}`}
          />
        </CCol>
        <CCol sm={6} lg={3}>
          <CWidgetStatsB
            className="resources-kpi h-100 kpi-pending"
            value={taskCounts.surveillances}
            title="Vigilâncias"
            text="Interfaces"
          />
        </CCol>
      </CRow>

      <CRow className="g-3 mb-4 align-items-stretch">
        <CCol lg={4}>
          <CCard className="resources-table-card dashboard-card">
            <CCardHeader className="resources-header">
              <div className="resources-section-title">Perfil</div>
            </CCardHeader>
            <CCardBody>
              <div className="d-flex align-items-center gap-3 mb-3">
                <CAvatar src={user.avatar_url || userAvatar} size="lg" />
                <div>
                  <div className="fw-semibold">{user.name}</div>
                  <div className="text-medium-emphasis small">{user.email}</div>
                </div>
              </div>
              {roleBadges.length ? (
                <div className="d-flex flex-wrap gap-2">
                  {roleBadges.map((role) => (
                    <CBadge key={role} color="info" className="projects-badge">
                      {role}
                    </CBadge>
                  ))}
                </div>
              ) : (
                <div className="text-muted small dashboard-empty">
                  Sem roles atribuídos.
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={8}>
          <CCard className="resources-table-card dashboard-card">
            <CCardHeader className="resources-header">
              <div className="resources-section-title">Departamentos</div>
            </CCardHeader>
            <CCardBody>
              {departments.length ? (
                <CListGroup flush>
                  {departments.map((dept) => (
                    <CListGroupItem
                      key={`${dept.department_id}-${dept.role}`}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <div className="fw-semibold">{dept.department}</div>
                        <div className="text-medium-emphasis small">
                          {dept.role}
                        </div>
                      </div>
                      {dept.responsible ? (
                        <CBadge color="success">Responsável</CBadge>
                      ) : null}
                    </CListGroupItem>
                  ))}
                </CListGroup>
              ) : (
                <div className="text-muted small dashboard-empty">
                  Sem departamentos associados.
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-4 align-items-stretch">
        <CCol lg={4}>
          <CCard className="resources-table-card dashboard-card">
            <CCardHeader className="resources-header">
              <div className="resources-section-title">
                Ações de melhoria abertas
              </div>
            </CCardHeader>
            <CCardBody>
              {data.tasks?.improvement_actions?.length ? (
                <CListGroup flush>
                  {data.tasks.improvement_actions.map((action) => (
                    <CListGroupItem key={action.id}>
                      <Link
                        to={action.link}
                        className="text-decoration-none fw-semibold"
                      >
                        {action.code}
                      </Link>
                      <div className="text-medium-emphasis small">
                        Prazo: {formatDate(action.deadline)}
                      </div>
                    </CListGroupItem>
                  ))}
                </CListGroup>
              ) : (
                <div className="text-muted small dashboard-empty">
                  Sem ações pendentes.
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={4}>
          <CCard className="resources-table-card dashboard-card">
            <CCardHeader className="resources-header">
              <div className="resources-section-title">Formações aprovadas</div>
            </CCardHeader>
            <CCardBody>
              {data.tasks?.events?.length ? (
                <CListGroup flush>
                  {data.tasks.events.map((event) => (
                    <CListGroupItem key={event.id}>
                      <Link
                        to={event.link}
                        className="text-decoration-none fw-semibold"
                      >
                        {event.designation}
                      </Link>
                      <div className="text-medium-emphasis small">
                        {formatDate(event.start_date)} •{" "}
                        {formatDate(event.end_date)}
                      </div>
                    </CListGroupItem>
                  ))}
                </CListGroup>
              ) : (
                <div className="text-muted small dashboard-empty">
                  Sem formações futuras.
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={4}>
          <CCard className="resources-table-card dashboard-card">
            <CCardHeader className="resources-header">
              <div className="resources-section-title">
                Avaliações pendentes
              </div>
            </CCardHeader>
            <CCardBody>
              {assessments.length ? (
                <CListGroup flush>
                  {assessments.map((assessment) => (
                    <CListGroupItem
                      key={`${assessment.type}-${assessment.id}`}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div>
                          <Link
                            to={assessment.link}
                            className="text-decoration-none fw-semibold"
                          >
                            {assessment.evaluatee}
                          </Link>
                          <div className="text-medium-emphasis small">
                            {assessment.department} • {assessment.year}
                          </div>
                        </div>
                        <CBadge color="info" className="projects-badge">
                          {assessment.type === "chefias"
                            ? "Chefias"
                            : "Desempenho"}
                        </CBadge>
                      </div>
                    </CListGroupItem>
                  ))}
                </CListGroup>
              ) : (
                <div className="text-muted small dashboard-empty">
                  Sem avaliações pendentes.
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard className="resources-table-card dashboard-card-lg mb-4">
        <CCardHeader className="resources-header">
          <div className="resources-section-title">
            Vigilâncias de interface
          </div>
        </CCardHeader>
        <CCardBody>
          {data.interface_surveillance?.length ? (
            <CListGroup flush>
              {data.interface_surveillance.map((surveillance) => (
                <CListGroupItem key={surveillance.id}>
                  <Link
                    to={surveillance.link}
                    className="text-decoration-none fw-semibold"
                  >
                    {surveillance.interface}
                  </Link>
                  <div className="text-medium-emphasis small">
                    {surveillance.area} • {surveillance.periodicity || "-"} •{" "}
                    {surveillance.year}
                  </div>
                </CListGroupItem>
              ))}
            </CListGroup>
          ) : (
            <div className="text-muted small dashboard-empty">
              Sem vigilâncias atribuídas.
            </div>
          )}
        </CCardBody>
      </CCard>
    </CContainer>
  );
};

export default Dashboard;
